import styled from "@emotion/styled/macro";
import {
  Pill,
  SvgNames,
  Tooltip,
  Typography,
} from "@sunrun/experience-ui-components";
import { tokens } from "@sunrun/experience-ui-components";

type BundleRadioButtonProps = {
  label: string;
  value: string;
  imageUrl: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  description: string;
  bundleId?: string;
  disqualifyingReasons?: string[];
  handleBundleChange: (value: string) => void;
};

const BundleRadioButton = ({
  label,
  value,
  imageUrl,
  isDisabled = false,
  isLoading = false,
  description,
  bundleId,
  disqualifyingReasons,
  handleBundleChange,
}: BundleRadioButtonProps) => {
  return (
    <BundleRadioButtonContainer isDisabled={isDisabled} isLoading={isLoading}>
      <StyledBundleRadioButton
        disabled={isDisabled || (isLoading && !(bundleId === value))}
        type="radio"
        name="bundleOptions"
        checked={bundleId === value}
        value={value}
        onChange={(event: any) => handleBundleChange(event.target.value)}
        data-testid={"standard-radio"}
      />
      <RadioInfo>
        <RadioImage imageUrl={imageUrl} isDisabled={isDisabled} />

        {isDisabled && !!disqualifyingReasons?.length && (
          <TooltipContainer>
            <Tooltip
              content={disqualifyingReasons.join("\n")}
              className="icon-tooltip"
              direction="bottom"
            >
              <CustomPill
                type="info"
                icon={SvgNames.InfoCircle}
                text="Bundle not available"
              />
            </Tooltip>
          </TooltipContainer>
        )}

        <TextContainer isDisabled={isDisabled}>
          <Typography variant="h6" color={tokens.BRAND_HERO_BLUE}>
            {label}
          </Typography>
          <Typography color={tokens.TINTS_OFF_WHITE_20}>
            {description}
          </Typography>
        </TextContainer>
      </RadioInfo>
    </BundleRadioButtonContainer>
  );
};

const BundleRadioButtonContainer = styled.div<{
  isDisabled: boolean;
  isLoading: boolean;
}>`
  cursor: ${(props) =>
    props.isLoading
      ? "progress"
      : props.isDisabled
      ? "not-allowed"
      : "pointer"};
  height: 100%;
`;

const StyledBundleRadioButton = styled.input`
  opacity: 0;
  position: absolute;

  &:not(:disabled) {
    &:checked + div,
    &:hover + div {
      border: 1.5px solid ${tokens.BRAND_HERO_BLUE};
      box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.12);
      border-radius: 6.144px;
    }
  }
`;

const RadioImage = styled.div<{ imageUrl: string; isDisabled: boolean }>`
  aspect-ratio: 21 / 9;
  background: ${(props) =>
    `${tokens.WHITE} url(${props.imageUrl}) no-repeat center`};
  background-size: cover;
  border-radius: 6.144px 6.144px 0 0;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
`;

const RadioInfo = styled.div`
  position: relative;
  border-radius: 6.144px;
  display: flex;
  flex-direction: column;
  border: 1.5px solid rgba(208, 204, 195, 0.6);
  background: ${tokens.TINTS_OFF_WHITE_70};
  height: inherit;
  width: 100%;
`;

const TextContainer = styled.div<{ isDisabled: boolean }>`
  padding: 16px;
  & > h6 {
    margin-bottom: 4px;
  }
  opacity: ${(props) => (props.isDisabled ? 0.7 : 1)};
`;

const TooltipContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
`;

const CustomPill = styled(Pill)`
  flex-direction: row-reverse !important;
  padding: 0px 20px !important;
  border-radius: 20px !important;
  border: none !important;

  p {
    font-size: 16px !important;
  }
`;

export { BundleRadioButton };
